import { useQueryClient } from "@tanstack/react-query";

import { useCurrentCompanyId } from "@/hooks/useCurrentCompanyId";
import { useCurrentJobId } from "@/hooks/useCurrentJobId";
import { useInvalidateNotifications } from "@/hooks/invalidate/useInvalidateNotifications";
import { useUpdateCandidacy } from "@/hooks/mutations/useUpdateCandidacy";
import { useGetCurrentUser } from "@/hooks/queries/useGetCurrentUser";
import { useSetCandidacyInAllLists } from "@/hooks/sets/useSetCandidacyInAllLists";
import { useSetCandidacyInfo } from "@/hooks/sets/useSetCandidacyInfo";
import { mixpanelUtils } from "@/lib/mixpanel/mixpanel";
import { MIXPANEL_SCREENS } from "@/lib/mixpanel/types";
import { CandidacyStatus, CandidacyStatusChange, ReviewDecision } from "@/types";

const decisionToStatus: Record<ReviewDecision, CandidacyStatus> = {
  [ReviewDecision.SHORTLIST]: CandidacyStatus.SHORTLIST,
  [ReviewDecision.SHORTLIST_MANUAL]: CandidacyStatus.SHORTLIST,
  [ReviewDecision.REJECT]: CandidacyStatus.REJECTED,
};

const decisionToButtonName: Record<ReviewDecision, string> = {
  [ReviewDecision.SHORTLIST]: "Shortlist Candidacy",
  [ReviewDecision.SHORTLIST_MANUAL]: "Shortlist Candidacy (Manual)",
  [ReviewDecision.REJECT]: "Reject Candidacy",
};

export function useApplicationReviewDecision() {
  const { mutate: updateCandidacy, isPending } = useUpdateCandidacy();
  const setCandidacyInAllLists = useSetCandidacyInAllLists();
  const setCandidacyInfo = useSetCandidacyInfo();
  const invalidateNotifications = useInvalidateNotifications();
  // Keyed off the decision's own candidacy rather than the ambient one: the
  // pipeline actions arbitrary rows with no candidacy sheet open.
  const currentCompanyId = useCurrentCompanyId();
  const currentJobId = useCurrentJobId();
  const currentUser = useGetCurrentUser();
  const queryClient = useQueryClient();

  const decide = ({
    candidacy,
    decision,
    deferDashboardInvalidate = false,
    onSuccess,
    screen,
    metadata,
  }: {
    candidacy: {
      id: string;
      lastCandidacyStatusChange: { toStatus: CandidacyStatus };
    };
    decision: ReviewDecision;
    // Set by hosts that batch one dashboard refetch when they close.
    deferDashboardInvalidate?: boolean;
    onSuccess?: (resolved: { status: CandidacyStatus }) => void;
    screen: MIXPANEL_SCREENS;
    metadata?: Record<string, unknown>;
  }) => {
    const fromStatus = candidacy.lastCandidacyStatusChange.toStatus;
    const toStatus = decisionToStatus[decision];
    mixpanelUtils.trackButtonClick(
      decisionToButtonName[decision],
      screen,
      metadata,
    );
    updateCandidacy(
      {
        candidacyId: candidacy.id,
        updateCandidacyRequest: {
          fromStatus,
          status: toStatus,
          // Manual mode shortlists without kicking off the AI scheduling agent.
          ...(decision === ReviewDecision.SHORTLIST_MANUAL && {
            agentShouldReachOut: false,
          }),
        },
      },
      {
        onSuccess: () => {
          const createdAt = new Date().toISOString();
          setCandidacyInAllLists({
            candidacyId: candidacy.id,
            deferDashboardInvalidate,
            lastCandidacyStatusChange: {
              createdAt,
              fromStatus,
              toStatus,
              statusChangeReason: null,
              byAgent: false,
            },
          });
          // The V2 sheet renders its status pill and logistics from
          // candidacy-info, and reads fromStatus back off the status-change
          // list — both go stale on undo without these.
          setCandidacyInfo({ candidacyId: candidacy.id, status: toStatus });
          queryClient.setQueryData(
            [
              "candidacy_status_changes",
              currentCompanyId,
              currentJobId,
              candidacy.id,
            ],
            (cached: CandidacyStatusChange[] | undefined) =>
              cached
                ? [
                    {
                      createdAt,
                      fromStatus,
                      statusChangeReason: null,
                      toStatus,
                      userId: currentUser?.id,
                    },
                    ...cached,
                  ]
                : cached,
          );
          // Shortlist/reject clears the "excellent candidate" notification on
          // the backend; refetch so the bell badge drops on this click.
          invalidateNotifications();
          onSuccess?.({ status: toStatus });
        },
      },
    );
  };

  return { decide, isPending };
}
